import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Phone,
  CreditCard,
  DollarSign,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function OwnersPage() {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rate: '',
    id_number: '',
    phone: '',
    is_active: true,
  });

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API_URL}/owners`);
      setOwners(response.data);
    } catch (error) {
      toast.error('Failed to fetch owners');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (owner = null) => {
    if (owner) {
      setSelectedOwner(owner);
      setFormData({
        name: owner.name,
        rate: owner.rate,
        id_number: owner.id_number || '',
        phone: owner.phone || '',
        is_active: owner.is_active,
      });
    } else {
      setSelectedOwner(null);
      setFormData({
        name: '',
        rate: '',
        id_number: '',
        phone: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedOwner) {
        await axios.put(`${API_URL}/owners/${selectedOwner.id}`, formData);
        toast.success('Owner updated successfully');
      } else {
        await axios.post(`${API_URL}/owners`, formData);
        toast.success('Owner created successfully');
      }
      setDialogOpen(false);
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save owner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/owners/${selectedOwner.id}`);
      toast.success('Owner deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedOwner(null);
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete owner');
    }
  };

  const filteredOwners = owners.filter((owner) => {
    return owner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (owner.id_number && owner.id_number.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Owners"
        subtitle={`Manage your ${owners.length} cane owners`}
        actions={
          <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-owner-btn">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Owner</span>
          </Button>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Search */}
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search owners..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-owners"
              />
            </div>
          </CardContent>
        </Card>

        {/* Owners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOwners.map((owner, index) => (
            <Card
              key={owner.id}
              className="card-base animate-slide-up hover:shadow-md transition-shadow"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`owner-card-${owner.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      owner.is_active ? 'bg-purple-500/10' : 'bg-muted'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        owner.is_active ? 'text-purple-600' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{owner.name}</h3>
                      <p className="text-sm text-muted-foreground">Owner</p>
                    </div>
                  </div>
                  <Badge variant={owner.is_active ? 'default' : 'secondary'}>
                    {owner.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="w-4 h-4" />
                    <span>Rate: {owner.rate}×</span>
                  </div>
                  {owner.id_number && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CreditCard className="w-4 h-4" />
                      <span>ID: {owner.id_number}</span>
                    </div>
                  )}
                  {owner.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{owner.phone}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenDialog(owner)}
                    data-testid={`edit-owner-${owner.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setSelectedOwner(owner);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-owner-${owner.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredOwners.length === 0 && (
          <Card className="card-base">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {searchQuery
                  ? 'No owners match your search.'
                  : 'No owners yet. Add your first owner to get started.'}
              </p>
              {!searchQuery && (
                <Button onClick={() => handleOpenDialog()} className="mt-4" data-testid="add-first-owner">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Owner
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedOwner ? 'Edit Owner' : 'Add New Owner'}
            </DialogTitle>
            <DialogDescription>
              {selectedOwner ? 'Update owner information.' : 'Add a new cane owner to your farm group.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter owner name"
                  required
                  data-testid="owner-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rate">Owner Rate (Multiplier) *</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  placeholder="1.0"
                  required
                  data-testid="owner-rate-input"
                />
                <p className="text-xs text-muted-foreground">
                  Amount = tons × grabs (e.g., 1.0 means full tons)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="id_number">ID Number</Label>
                <Input
                  id="id_number"
                  value={formData.id_number}
                  onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                  placeholder="ID-12345"
                  data-testid="owner-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+501 123-4567"
                  data-testid="owner-phone-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-input"
                  data-testid="owner-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Active Owner
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-owner-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Owner'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Owner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedOwner?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-owner"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
